import * as _ from 'lodash';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { TClientAffixData, THeroSubTree } from '../../../types/keystoneAffixTypes';
import { FeatureFlagMap } from '../../../common/enums';
import { TPatronLevel } from '../../../common/patron';
import { IXsollaPublicSiteConfig } from '../../../common/xsolla';
import { groupByDictUnique } from '../../../common/utils';
import { getGameConfig } from '../../../gameConfig';
import { Expansion } from '../../../gameConfig/expansion';
import { TArenaSummary, TDungeonSummary, TRaidSummary } from '../../../types/raidTypes';
import { TClassSummary, TRaceSummary, TRegionSummary, TSpecSummary } from '../../../types/utilityTypes';
import { findSpecAndClassBySlugs } from '../../libs/siteMeta';
import { FeatureFlag } from '../../../gameConfig/sharedTypes';
import { KeystoneAffixHelper } from '../../../common/affixHelpers/KeystoneAffixHelper';
import { TClaimedCharacter } from '../../../types/characterTypes';
import { TRecruitmentTag } from '../../../types/recruitmentTypes';

const gameConfig = getGameConfig();

export type TCommonSiteMeta = {
  allSites: any;
  site: any;
  game: 'wowclassic' | 'wowera' | 'wowretail';
  regions: TRegionSummary[];
  subRegions: TRegionSummary[];
  races: TRaceSummary[];
  classes: TClassSummary[];
  specs: TSpecSummary[];
  raids: TRaidSummary[];
  dungeons: TDungeonSummary[];
  arenas: TArenaSummary[];
  seasons: any[];
  affixes: TClientAffixData[];
  recruitmentTags: TRecruitmentTag[];
  manualClaimAllowAnyRegion?: boolean;
  heroSubTrees?: Record<number, THeroSubTree>;
};

export type TCommonSiteConfig = {
  siteMetaUrl: string;
  localeConfigUrl: string;
  staticFeatures: any;
  assetsBaseUri: string;
  maintenance: boolean;
  isEmbedded: boolean;
  versionCode: string;
  seed: number; // random seed for page load
  seed2: number; // random seed for page load
  userSeed: number; // deterministic seed based on logged in user (if logged in-- otherwise random)
  patronLevel: TPatronLevel | null;
  isImpersonating?: boolean;
  videoAdsProvider?: string;
  displayAdsProvider?: string;
  serverTimeMs: number;
  militaryTime: number;
  enableReamaze?: boolean;
  streamers: {
    count: number;
  };
  streamingGuilds: {
    count: number;
  };
  streamingTeams: {
    count: number;
  };
  banners: any[];
  houseAds: any;
  maintenanceFeatures: any;
  consentManagementEnabled: boolean;
  country: string;
  venatusAdsVersion?: string;
  connatixConfig?: {
    embedScriptUrl: string;
    playerElementId: string;
  };

  currentExpansionId: Expansion;

  currentSeasonSlug: string;
  currentSeasonDungeons: TDungeonSummary[];
  currentSeasonCutoffSlug: string;

  currentRaidSlug: string;
  currentRaidEndBossSlug: string;
  currentRaidDifficulty: string;
  currentRaidTier: string;

  previousSeasonSlug: string;
  previousRaidSlug: string;
  nextRaidSlug: string;
  nextSeasonSlug: string;

  homePageRaidSlug: string;
  homePageRaidDifficulty: string;
  homePageSeasonSlug: string;

  // Xsolla configuration for subscription payments
  xsolla?: IXsollaPublicSiteConfig;
};

export const featuresSelector = (state: any): FeatureFlagMap => state.currentFeatures;
export const siteConfigSelector = (state: any): TCommonSiteConfig => state.siteConfig;
export const siteMetaSelector = (state: any): TCommonSiteMeta => state.siteMeta;

export const patronLevelSelector = createSelector(
  siteConfigSelector,
  ({ patronLevel }) => patronLevel as TPatronLevel | null
);

export const authStateSelector = (state: any) => state.auth;

export const allRegionsSelector = createSelector(siteMetaSelector, ({ regions }) => regions as TRegionSummary[]);

export const classesSelector = createSelector(siteMetaSelector, ({ classes }) => classes as TClassSummary[]);

export const racesSelector = createSelector(siteMetaSelector, ({ races }) => races as TRaceSummary[]);

export const specsSelector = createSelector(siteMetaSelector, ({ specs }) => specs as TSpecSummary[]);

export const seasonsSelector = createSelector(siteMetaSelector, ({ seasons }) => seasons);

export const currentSeasonSlugSelector = createSelector(
  siteConfigSelector,
  ({ currentSeasonSlug }) => currentSeasonSlug
);

export const affixesSelector = createSelector(siteMetaSelector, ({ affixes }) => affixes);

export const specsByRoleSelector = createSelector(specsSelector, (allSpecs) =>
  _.reduce(
    allSpecs,
    (groupedSpecList: { [rk: string]: TSpecSummary[] }, spec) => {
      const roleKey = spec.role === 'dps' ? `dps-${spec.is_melee ? 'melee' : 'ranged'}` : spec.role;

      groupedSpecList[roleKey].push(spec);

      return groupedSpecList;
    },
    { tank: [], healer: [], 'dps-melee': [], 'dps-ranged': [] }
  )
);

export const allRaidsSelector = createSelector(siteMetaSelector, ({ raids }) => raids);
export const allDungeonsSelector = createSelector(siteMetaSelector, ({ dungeons }) => dungeons);
export const allArenasSelector = createSelector(siteMetaSelector, ({ arenas }) => arenas);

// return the raids from the current expansion
export const currentRaidsSelector = createSelector(siteMetaSelector, ({ raids }) =>
  raids.filter((r: any) => r.expansion_id === gameConfig.CurrentExpansion)
);

export const timezoneSelector = createSelector(authStateSelector, (auth) => auth?.timezone || '');

// claimed characters that can be used by the recruitment system
// @jah, i'm adding this comment to remember to ask you about this. Since you added that this is to
// be used by the recruitment system, this should live in a recruitment route's selectors. Otherwise,
// this file ends up getting populated with a lot of content that isn't truly global.
// I wanna make sure we are on the same page before I move it
export const claimedCharactersSelector = createSelector(authStateSelector, ({ claimedCharacters }) =>
  claimedCharacters.filter(
    (ch: TClaimedCharacter) => ch.details.level >= gameConfig.PreviousLevelCap && ch.details.persona_id > 0
  )
);

export const claimedCharacterRegionsSelector = createSelector(authStateSelector, ({ claimedCharacters }): string[] =>
  _.uniq(claimedCharacters.map((ch: TClaimedCharacter) => ch.region.slug))
);

export const isChinaUserSelector = createSelector(authStateSelector, ({ isChinaUser }) => isChinaUser);

export const allClaimedCharactersSelector = createSelector(
  authStateSelector,
  ({ claimedCharacters }) => claimedCharacters
);

export const claimedGuildsSelector = createSelector(authStateSelector, (auth) => auth?.memberGuildGroups || []);

export const userIdSelector = createSelector(authStateSelector, (auth: any): number | undefined => auth?.user_id);
export const userRegionsSelector = (state: any): string[] => {
  const linkedRegions = state.auth?.bnet_linked_regions;

  if (linkedRegions?.length) {
    if (linkedRegions.length === 1) {
      return linkedRegions;
    }

    const starts = state.siteMeta.seasons[0].starts;
    return linkedRegions.sort((regionA: string, regionB: string) => {
      return moment.utc(starts[regionA]).valueOf() - moment.utc(starts[regionB]).valueOf();
    });
  }

  return state.siteMeta.regions.filter((region: any) => region.slug !== 'world').map((region: any) => region.slug);
};

//
// Hooks
//
export const useFeatures = () => useSelector(featuresSelector);
export const useFeature = (feature: FeatureFlag) => useSelector((state: any) => state.currentFeatures[feature]);
export const useIsAdmin = () => useFeature(FeatureFlag.ADMIN);
export const useCurrentUserId = () => useSelector((state: any) => state.auth?.user_id || null) as number | null;

export const useIsAuthenticated = () => useFeature(FeatureFlag.AUTHENTICATED);
export const useCurrentUser = () =>
  useSelector((state: any) => (state.auth ? { name: state.auth.name, id: state.auth.user_id } : null));
export const useCurrentUserAvatar = () => useSelector((state: any) => state.auth?.avatar || null) as string | null;
export const useUserIsPremium = () => {
  const features = useFeatures();
  return [
    FeatureFlag.PREMIUM_1,
    FeatureFlag.PREMIUM_2,
    FeatureFlag.PREMIUM_3,
    FeatureFlag.PREMIUM_4,
    FeatureFlag.PREMIUM_5,
    FeatureFlag.ADMIN,
    FeatureFlag.SUPPORT_TEAM,
    FeatureFlag.PREMIUM
  ].some((feature) => features[feature]);
};

export const useSiteConfig = () => useSelector(siteConfigSelector);
export const useSiteMeta = () => useSelector(siteMetaSelector);
export const useAllRaids = () => useSelector(allRaidsSelector);
export const useAllDungeons = () => useSelector(allDungeonsSelector);
export const useAllArenas = () => useSelector(allArenasSelector);
export const useAllSpecs = () => useSelector(specsSelector);
export const useAllClasses = () => useSelector(classesSelector);
export const useAllRaces = () => useSelector(racesSelector);
export const useClassesById = () => {
  const classes = useAllClasses();
  return useMemo(() => groupByDictUnique(classes, 'id'), [classes]);
};
export const useClassById = (classId?: number) => {
  const classes = useClassesById();
  return classId ? classes[classId] : undefined;
};
export const useAllAffixes = () => useSelector(affixesSelector);

export const useAffixesBySlug = () => {
  const affixes = useAllAffixes();
  return useMemo(() => groupByDictUnique(affixes, 'slug'), [affixes]);
};

export const useSpecsById = () => {
  const specs = useAllSpecs();
  return useMemo(() => groupByDictUnique(specs, 'id'), [specs]);
};

export function getFeatureParam(features: FeatureFlagMap, paramKey: string): string | undefined;
export function getFeatureParam<T>(
  features: FeatureFlagMap,
  paramKey: string,
  mapper: (value: string | undefined) => T
): T;
export function getFeatureParam<T>(
  features: FeatureFlagMap,
  paramKey: string,
  mapper?: (value: string | undefined) => T
): T | string | undefined {
  const paramValue = features.params[paramKey];
  if (mapper) {
    return mapper(paramValue);
  }
  return paramValue;
}

export function useFeatureParam(paramKey: string): string | undefined;
export function useFeatureParam<T>(paramKey: string, mapper: (value: string | undefined) => T): T;
export function useFeatureParam<T>(
  paramKey: string,
  mapper?: (value: string | undefined) => T
): T | string | undefined {
  const features = useFeatures();
  return getFeatureParam(features, paramKey, mapper as any);
}

export const useAllRegions = () => useSelector(allRegionsSelector);

export const useRegionBySlug = (regionSlug?: string) => {
  const regions = useAllRegions();
  return regionSlug ? regions.find((region) => region.slug === regionSlug) : undefined;
};

export const useSpecBySlug = (specSlug?: string, classSlug?: string) => {
  const specs = useAllSpecs();
  const classes = useAllClasses();

  return useMemo(() => {
    if (!specSlug || !classSlug) {
      return { spec: null, cls: null };
    }
    return findSpecAndClassBySlugs(specs, classes, specSlug, classSlug);
  }, [specs, classes, specSlug, classSlug]);
};

export const useSpecById = (specId?: number) => {
  const specs = useAllSpecs();
  const classes = useAllClasses();
  return useMemo(() => {
    if (!specId) {
      return null;
    }
    const spec = specs.find((spec) => spec.id === specId);
    if (!spec) {
      return null;
    }
    return { spec, cls: classes.find((cls) => cls.id === spec.class_id)! };
  }, [specs, specId]);
};

export const useClassBySlug = (classSlug?: string) => {
  const classes = useAllClasses();
  return classSlug ? classes.find((cls) => cls.slug === classSlug) : undefined;
};

export const useRandomSpecWithClass = () => {
  const specs = useAllSpecs();
  const classes = useAllClasses();

  return useMemo(() => {
    const randomSpec = _.sample(specs)!;
    const cls = classes.find((c) => c.id === randomSpec.class_id)!;
    return { spec: randomSpec, cls };
  }, [specs, classes]);
};

export const useZones = () => {
  const dungeons = useAllDungeons();
  const arenas = useAllArenas();

  const zones = [...dungeons, ...arenas];

  const getZoneById = useCallback(
    (zoneId: number) => {
      return zones.find((zone) => zone.id === zoneId);
    },
    [zones]
  );

  const getZoneBySlug = useCallback(
    (zoneSlug: string) => {
      return zones.find((zone) => zone.slug === zoneSlug);
    },
    [zones]
  );

  return useMemo(() => ({ zones, getZoneById, getZoneBySlug }), [zones, getZoneById, getZoneBySlug]);
};

export const useKeystoneAffixesHelper = (): KeystoneAffixHelper<TClientAffixData> => {
  const affixes = useSelector(affixesSelector);
  return useMemo(() => new KeystoneAffixHelper(affixes.filter((afx) => !!afx.slug)), [affixes]);
};

export const useIsEmbedded = () => {
  const siteConfig = useSiteConfig();
  return siteConfig.isEmbedded;
};
